import { useState, FC, useRef, useCallback } from 'react';
import {
  AnonymousValueCodeOrderErrorCode,
  buyGiftCardPageQuery_buyGiftCardPage,
  buyGiftCardPageQuery_products,
  payGiftCardOrderChargeMutation,
  payGiftCardOrderChargeMutationVariables,
  payGiftCardOrderCheckoutMutation,
  payGiftCardOrderCheckoutMutationVariables,
  PaymentDocumentType,
  payOrderChargeMutation_payOrderCharge_ConfirmationSessionStripe,
  payOrderCheckoutMutation_payOrderCheckout_CheckoutSessionStripe,
  placeAnonymousValueCodeOrderMutation,
  placeAnonymousValueCodeOrderMutationVariables,
  placeValueCodeOrderMutation,
  placeValueCodeOrderMutationVariables,
  ValueCodeOrderErrorCode,
  applyDiscountCodeMutation_applyDiscountCode_DiscountCodeError,
  applyDiscountCodeMutation,
  applyDiscountCodeMutationVariables,
  applyDiscountCodeMutation_applyDiscountCode_Order,
  revokeDiscountCodeMutation,
  revokeDiscountCodeMutationVariables,
  PaymentMethodType
} from '../../core-types';
import {
  Alert,
  Anchor,
  AnchorButton,
  Box,
  Container,
  Flex,
  FormGroup,
  H1,
  Label,
  LoadableButton,
  Loading,
  Text,
  CheckboxStateless,
  Input,
  InputGroupAddon,
  InputGroup,
  I18NRemove,
  Spinner
} from '../../components';
import {
  FieldArray,
  Form,
  Formik,
  Field,
  FieldProps,
  ArrayHelpers
} from 'formik';
import GiftCards from './components/GiftCards';
import { useSettingsQuery } from '../../lib/core/SettingsQuery';
import { FormattedMessage, useIntl } from 'react-intl';
import { FormattedCurrency } from '../../components/FormattedCurrency';
import { getSum, getProduct } from './components/getSum';
import { messages } from './components/messages';
import {
  PAY_GIFT_CARD_ORDER_CHARGE_MUTATION,
  PAY_GIFT_CARD_ORDER_CHECKOUT_MUTATION,
  PLACE_ANON_VALUE_CODE_ORDER,
  PLACE_VALUE_CODE_ORDER
} from './graphql';
import { useCurrentUser } from '../../lib/core/useCurrentUser';
import { useMutation } from '@apollo/client';
import { StripeCheckoutRedirect } from '../StripeCheckoutRedirect';
import { ChargePaymentCardMessage } from '../../components/ChargePaymentCardMessage';
import { pushRoute, replaceRoute } from '../../components/Link';
import { StripeConfirmCardPayment } from '../StripeConfirmCardPayment';
import { PaymentProviderInfo } from '../../components/PaymentProviderInfo';
import { validate, Values } from './validate';
import { useSystem } from '../../lib/core/useSystem';
import { getQueryString } from '../../lib/utils';
import { useAsyncProcessCompletion } from '../AsyncProcessCompletion';
import { useRouter } from 'next/router';
import { TermsOfSale } from './TermsOfSale';
import { Valuecode, Warning } from '@urbaninfrastructure/react-icons';
import { AnimatePresence, motion } from 'framer-motion';
import {
  APPLY_DISCOUNT_CODE_MUTATION,
  REVOKE_DISCOUNT_CODE_MUTATION
} from '../BuyProduct/Step/graphql';
import { PaymentMethodTypeList } from '../../components/PaymentMethodTypeList';

export const BuyGiftCardsPayment2 = ({
  buyGiftCardPage,
  products
}: {
  buyGiftCardPage: buyGiftCardPageQuery_buyGiftCardPage;
  products: buyGiftCardPageQuery_products[];
}) => {
  const intl = useIntl();
  const router = useRouter();
  const [hidden, setHidden] = useState(true);
  const [discountApplied, setDiscountApplied] = useState(false);
  const {
    currentUser,
    loading: currentUserLoading,
    refetch
  } = useCurrentUser();
  const settingsQueryResult = useSettingsQuery();
  const system = useSystem();
  const privacyPageSlug =
    settingsQueryResult.data?.settings.privacyPageSlug?.current;
  const inputEl = useRef(null);

  const [applyDiscountCode, applyDiscountCodeResult] = useMutation<
    applyDiscountCodeMutation,
    applyDiscountCodeMutationVariables
  >(APPLY_DISCOUNT_CODE_MUTATION);

  const [checkoutSessionStripe, setCheckoutSessionStripe] = useState<
    payOrderCheckoutMutation_payOrderCheckout_CheckoutSessionStripe | undefined
  >();
  const [valueCodeOrderErrorCode, setValueCodeOrderErrorCode] = useState<
    AnonymousValueCodeOrderErrorCode | ValueCodeOrderErrorCode | undefined
  >();
  const [
    confirmationSessionStripeData,
    setConfirmationSessionStripeData
  ] = useState<
    payOrderChargeMutation_payOrderCharge_ConfirmationSessionStripe | undefined
  >();
  const [
    confirmationSessionStripeError,
    setConfirmationSessionStripeError
  ] = useState<boolean>(false);
  const [paymentError, setPaymentError] = useState<string | undefined>();
  const [payGiftCardOrderCheckout, payOrderCheckoutResult] = useMutation<
    payGiftCardOrderCheckoutMutation,
    payGiftCardOrderCheckoutMutationVariables
  >(PAY_GIFT_CARD_ORDER_CHECKOUT_MUTATION, {
    onCompleted: data => {
      switch (data.payOrderCheckout.__typename) {
        case 'CheckoutSessionStripe': {
          setCheckoutSessionStripe(data.payOrderCheckout);
          break;
        }
        case 'CheckoutSessionPayex': {
          window.location.replace(data.payOrderCheckout.redirectUrl);
          break;
        }
      }
    }
  });

  const token = getQueryString(router.query.token);

  const handleToggle = () => {
    setHidden(prevOpen => !prevOpen);
  };

  const asyncProcessCompletionResult = useAsyncProcessCompletion({
    token,
    type: PaymentDocumentType.order,
    paymentState: 'success',
    skip: !token,
    onAsyncProcessCompleted: state => {
      switch (state) {
        case 'success': {
          replaceRoute({
            intl,
            route: 'order-confirmation',
            params: {
              token,
              locale: intl.locale
            }
          });
          // refetch currentUser
          refetch();
          break;
        }
        case 'cancelled': {
          // do nothing
          break;
        }
        case 'failed':
        case 'error': {
          // error is shown in the UI
          break;
        }
      }
    }
  });

  const [
    anonymousOrderPaymentMethodType,
    setAnonymousOrderPaymentMethodType
  ] = useState<PaymentMethodType | null>(null);

  const [
    placeAnonymousValueCodeOrder,
    placeAnonymousValueCodeOrderResult
  ] = useMutation<
    placeAnonymousValueCodeOrderMutation,
    placeAnonymousValueCodeOrderMutationVariables
  >(PLACE_ANON_VALUE_CODE_ORDER, {
    onCompleted: data => {
      switch (data.placeAnonymousValueCodeOrder.__typename) {
        case 'Order': {
          payGiftCardOrderCheckout({
            variables: {
              paths: getPaths({
                asPath: router.asPath,
                token: data.placeAnonymousValueCodeOrder.token,
                type: PaymentDocumentType.order
              }),
              orderId: data.placeAnonymousValueCodeOrder.id,
              paymentMethodType: anonymousOrderPaymentMethodType
            }
          });
          break;
        }
        case 'AnonymousValueCodeOrderError': {
          setValueCodeOrderErrorCode(
            data.placeAnonymousValueCodeOrder.errorCode
          );
        }
      }
    }
  });
  const [placeValueCodeOrder, placeValueCodeOrderResult] = useMutation<
    placeValueCodeOrderMutation,
    placeValueCodeOrderMutationVariables
  >(PLACE_VALUE_CODE_ORDER);

  const [
    placeValueCodeOrderWithDiscount,
    placeValueCodeOrderWithDiscountResult
  ] = useMutation<
    placeValueCodeOrderMutation,
    placeValueCodeOrderMutationVariables
  >(PLACE_VALUE_CODE_ORDER, {
    onCompleted: ({ placeValueCodeOrder }) => {
      const discountCodeInput = inputEl.current;
      if (placeValueCodeOrder.__typename === 'Order') {
        if (discountCodeInput) {
          applyDiscountCode({
            variables: {
              orderId: placeValueCodeOrder.id,
              code: discountCodeInput?.['value']
            }
          });
          setDiscountApplied(true);
        }
      }
    }
  });

  const [revokeDiscountCode, revokeDiscountCodeResult] = useMutation<
    revokeDiscountCodeMutation,
    revokeDiscountCodeMutationVariables
  >(REVOKE_DISCOUNT_CODE_MUTATION, {
    onCompleted: () => {
      setDiscountApplied(false);
    }
  });

  const [
    placeValueCodeOrderCheckout,
    placeValueCodeOrderCheckoutResult
  ] = useMutation<
    placeValueCodeOrderMutation,
    placeValueCodeOrderMutationVariables
  >(PLACE_VALUE_CODE_ORDER, {
    onCompleted: data => {
      if (data.placeValueCodeOrder.__typename === 'Order') {
        payGiftCardOrderCheckout({
          variables: {
            paths: getPaths({
              asPath: router.asPath,
              token: data.placeValueCodeOrder.token,
              type: PaymentDocumentType.order
            }),
            orderId: data.placeValueCodeOrder.id
          }
        });
      }
      if (data.placeValueCodeOrder.__typename === 'ValueCodeOrderError') {
        setValueCodeOrderErrorCode(data.placeValueCodeOrder.errorCode);
      }
    }
  });

  const [payGiftCardOrderCharge, payOrderChargeResult] = useMutation<
    payGiftCardOrderChargeMutation,
    payGiftCardOrderChargeMutationVariables
  >(PAY_GIFT_CARD_ORDER_CHARGE_MUTATION, {
    onCompleted: data => {
      switch (data.payOrderCharge.__typename) {
        case 'PaymentIntentError': {
          setPaymentError(data.payOrderCharge.message);
          break;
        }
        case 'ChargeCompletedSubscription': {
          break;
        }
        case 'ChargeCompleted': {
          pushRoute({
            intl,
            route: 'order-confirmation',
            params: {
              token: data.payOrderCharge.paymentDocument.token,
              locale: intl.locale
            }
          });
          break;
        }
        case 'ConfirmationSessionStripe': {
          setConfirmationSessionStripeData(data.payOrderCharge);
          break;
        }
        case 'RequirePaymentMethodStripe': {
          //TODO: only used for native apps
          break;
        }
      }
    }
  });

  let discountError:
    | applyDiscountCodeMutation_applyDiscountCode_DiscountCodeError
    | undefined;
  let discountedOrder:
    | applyDiscountCodeMutation_applyDiscountCode_Order
    | undefined;
  if (
    applyDiscountCodeResult.data &&
    applyDiscountCodeResult.data.applyDiscountCode
  ) {
    if (
      applyDiscountCodeResult.data.applyDiscountCode.__typename ===
      'DiscountCodeError'
    ) {
      discountError = applyDiscountCodeResult.data?.applyDiscountCode;
    } else if (
      applyDiscountCodeResult.data.applyDiscountCode.__typename === 'Order'
    ) {
      discountedOrder = applyDiscountCodeResult.data.applyDiscountCode;
    }
  }

  const loading =
    payOrderCheckoutResult.loading ||
    placeAnonymousValueCodeOrderResult.loading ||
    placeValueCodeOrderResult.loading ||
    payOrderChargeResult.loading ||
    applyDiscountCodeResult.loading ||
    revokeDiscountCodeResult.loading;

  const error =
    payOrderCheckoutResult.error ||
    placeAnonymousValueCodeOrderResult.error ||
    placeValueCodeOrderResult.error ||
    payOrderChargeResult.error ||
    placeValueCodeOrderCheckoutResult.error ||
    placeValueCodeOrderWithDiscountResult.error;

  const asyncProcessCompletionLoading =
    asyncProcessCompletionResult.loading ||
    asyncProcessCompletionResult.postMessageState === 'success';

  const { availablePaymentMethods } = system;
  const hasMultiplePaymentMethodTypes = availablePaymentMethods.length > 1;

  const onFormSubmit = useCallback(
    async (values: Values) => {
      const variables = {
        orderLines: values.orderLines,
        customerEmail: values.customerEmail,
        receiptEmail: !hidden ? values.receiptEmail : ''
      };

      if (!currentUser) {
        setAnonymousOrderPaymentMethodType(values.paymentMethodType);
        return placeAnonymousValueCodeOrder({ variables });
      }

      if (discountApplied && !discountError) {
        if (currentUser.paymentMethod) {
          setConfirmationSessionStripeError(false);
          setConfirmationSessionStripeData(undefined);
          return payGiftCardOrderCharge({
            variables: {
              orderId: discountedOrder?.id || ''
            }
          });
        }
        return payGiftCardOrderCheckout({
          variables: {
            orderId: discountedOrder?.id || '',
            paths: getPaths({
              asPath: router.asPath,
              token: discountedOrder?.token || '',
              type: PaymentDocumentType.order
            }),
            paymentMethodType: values.paymentMethodType
          }
        });
      }

      const { data } = await placeValueCodeOrder({ variables });
      if (!data) {
        return;
      }

      if (data.placeValueCodeOrder.__typename === 'ValueCodeOrderError') {
        return setValueCodeOrderErrorCode(data.placeValueCodeOrder.errorCode);
      }
      if (data.placeValueCodeOrder.__typename !== 'Order') {
        return;
      }

      if (currentUser.paymentMethod) {
        setConfirmationSessionStripeError(false);
        setConfirmationSessionStripeData(undefined);
        return payGiftCardOrderCharge({
          variables: {
            orderId: data.placeValueCodeOrder.id
          }
        });
      }

      return payGiftCardOrderCheckout({
        variables: {
          orderId: data.placeValueCodeOrder.id,
          paths: getPaths({
            asPath: router.asPath,
            token: data.placeValueCodeOrder.token,
            type: PaymentDocumentType.order
          }),
          paymentMethodType: values.paymentMethodType
        }
      });
    },
    [
      currentUser,
      discountedOrder,
      hidden,
      discountApplied,
      discountError,
      router,
      placeValueCodeOrder,
      placeAnonymousValueCodeOrder,
      payGiftCardOrderCharge,
      payGiftCardOrderCheckout
    ]
  );

  return (
    <Box>
      <H1 textAlign="center">{buyGiftCardPage.heading}</H1>
      <Container variant="narrow">
        {asyncProcessCompletionLoading && (
          <Flex alignItems="center" justifyContent="center">
            <Loading />
            <FormattedMessage
              id="paymentProcessing"
              defaultMessage="Processing payment…"
            />
          </Flex>
        )}
        {(asyncProcessCompletionResult.postMessageState === 'error' ||
          asyncProcessCompletionResult.postMessageState === 'failed') && (
          <Alert variant="error">
            <FormattedMessage
              id="paymentError"
              defaultMessage="Could not process payment."
            />
          </Alert>
        )}
        {!asyncProcessCompletionLoading && (
          <>
            {paymentError && (
              <Alert variant="error" mb="sm">
                {paymentError}
              </Alert>
            )}
            {(error || valueCodeOrderErrorCode) && (
              <Alert variant="error" mb="sm">
                <FormattedMessage
                  id="modules.BuyGiftCardsForm.submitError"
                  defaultMessage="Could not submit order, please try again."
                />
              </Alert>
            )}
            {checkoutSessionStripe && (
              <StripeCheckoutRedirect
                sessionId={checkoutSessionStripe.sessionId}
                publishableKey={checkoutSessionStripe.publishableKey}
                showSpinner={false}
              />
            )}
            {confirmationSessionStripeData && (
              <StripeConfirmCardPayment
                showSpinner={false}
                clientSecret={confirmationSessionStripeData.clientSecret}
                paymentMethod={confirmationSessionStripeData.paymentMethodRef}
                token={confirmationSessionStripeData.paymentDocument.token}
                type={PaymentDocumentType.order}
                publishableKey={confirmationSessionStripeData.publishableKey}
                onError={() => {
                  setConfirmationSessionStripeError(true);
                  setConfirmationSessionStripeData(undefined);
                }}
                onAsyncProcessCompleted={state => {
                  switch (state) {
                    case 'success': {
                      setConfirmationSessionStripeError(false);
                      pushRoute({
                        intl,
                        route: 'order-confirmation',
                        params: {
                          token:
                            confirmationSessionStripeData?.paymentDocument
                              .token,
                          locale: intl.locale
                        }
                      });
                      break;
                    }
                    case 'error':
                    case 'cancelled':
                    case 'failed': {
                      setConfirmationSessionStripeData(undefined);
                      setConfirmationSessionStripeError(true);
                      break;
                    }
                  }
                }}
              />
            )}
            {confirmationSessionStripeError && (
              <Alert variant="error">
                <FormattedMessage
                  id="StripeCheckout.confirmationSessionStripe.error"
                  defaultMessage="Could not confirm your payment card. Please try again or add a new payment card."
                />
              </Alert>
            )}
            <Formik<Values>
              initialValues={{
                customerEmail: (currentUser && currentUser.email) || '',
                receiptEmail: (currentUser && currentUser.email) || '',
                tosAccepted: false,
                orderLines: [],
                paymentMethodType: hasMultiplePaymentMethodTypes
                  ? null
                  : availablePaymentMethods.length[0] || PaymentMethodType.card
              }}
              enableReinitialize={true}
              validateOnMount={true}
              validate={validate}
              onSubmit={onFormSubmit}
            >
              {({
                values,
                handleChange,
                handleBlur,
                setFieldValue,
                isValid
              }) => {
                const sum = getSum(products, values.orderLines);
                const product = getProduct(products, values.orderLines);
                const orderLineQuantity = values.orderLines.map(
                  orderLine => orderLine['quantity']
                );
                const giftCardsSelected =
                  orderLineQuantity.length > 1 || orderLineQuantity[0] > 1;

                const handleApplyDiscountClick = () =>
                  placeValueCodeOrderWithDiscount({
                    variables: {
                      orderLines: [
                        {
                          productId: product?.['id'],
                          quantity: 1
                        }
                      ],
                      customerEmail: values.customerEmail,
                      receiptEmail: values.receiptEmail
                        ? values.receiptEmail
                        : ''
                    }
                  });

                const handleRevokeDiscountClick = () =>
                  revokeDiscountCode({
                    variables: {
                      orderId: discountedOrder?.id || ''
                    }
                  });

                const handleChangeClick = () =>
                  placeValueCodeOrderCheckout({
                    variables: {
                      orderLines: values.orderLines,
                      customerEmail: values.customerEmail,
                      receiptEmail: !hidden ? values.receiptEmail : ''
                    }
                  });

                const renderOrderLines = (arrayHelpers: ArrayHelpers) => (
                  <div>
                    <GiftCards
                      products={products}
                      orderLines={values.orderLines}
                      onAddRemoveClick={(productId, quantity) => {
                        const productIndex = values.orderLines.findIndex(
                          orderLine => orderLine.productId === productId
                        );
                        if (productIndex === -1) {
                          arrayHelpers.push({ productId, quantity });
                        } else {
                          const orderLine = values.orderLines[productIndex];
                          const newQuantity = orderLine.quantity + quantity;
                          if (newQuantity === 0) {
                            arrayHelpers.remove(productIndex);
                          } else {
                            arrayHelpers.replace(productIndex, {
                              productId,
                              quantity: newQuantity
                            });
                          }
                        }
                      }}
                      locked={
                        currentUserLoading ||
                        (discountApplied && !discountError)
                      }
                    />
                  </div>
                );

                const renderDiscountCodeField = (fieldProps: FieldProps) => {
                  return (
                    <Box mb={5}>
                      <Label htmlFor="discount-code">
                        <FormattedMessage
                          id="discount_code"
                          defaultMessage="Discount code"
                        />
                      </Label>
                      <AnimatePresence>
                        {discountError && !applyDiscountCodeResult.loading && (
                          <motion.div
                            key="discount-error"
                            initial={{ y: 50, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 0, opacity: 0 }}
                          >
                            <FormError>{discountError.message}</FormError>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      <Flex
                        alignItems="center"
                        position="relative"
                        overflow="hidden"
                      >
                        <InputGroup>
                          <InputGroupAddon
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Valuecode color="primary" />
                          </InputGroupAddon>
                          <Input
                            id="discount-code"
                            disabled={giftCardsSelected}
                            ref={inputEl}
                            alternative={false}
                            {...fieldProps.field}
                          />
                        </InputGroup>
                        <AnimatePresence>
                          {fieldProps.form.values.code && (
                            <MotionBox
                              key="apply-button"
                              initial={{ x: 100, opacity: 0 }}
                              animate={{ x: 0, opacity: 1 }}
                              exit={{ x: 100, opacity: 0 }}
                              style={{
                                zIndex: 2,
                                position: 'absolute',
                                right: 10
                              }}
                            >
                              {!discountApplied || discountError ? (
                                <LoadableButton
                                  disabled={
                                    orderLineQuantity.length > 1 ||
                                    orderLineQuantity[0] > 1
                                  }
                                  onClick={handleApplyDiscountClick}
                                  name="apply-discount"
                                  loading={revokeDiscountCodeResult.loading}
                                  responseState={
                                    discountError ||
                                    revokeDiscountCodeResult.error
                                      ? 'error'
                                      : 'success'
                                  }
                                  size="small"
                                  shape="cool"
                                  variant="primary"
                                  ml={2}
                                  py={2}
                                >
                                  <FormattedMessage
                                    id="discount_apply"
                                    defaultMessage="Apply"
                                  />
                                </LoadableButton>
                              ) : (
                                <LoadableButton
                                  onClick={handleRevokeDiscountClick}
                                  name="revoke-discount"
                                  loading={applyDiscountCodeResult.loading}
                                  responseState={
                                    discountError ||
                                    applyDiscountCodeResult.error
                                      ? 'error'
                                      : 'success'
                                  }
                                  size="small"
                                  shape="cool"
                                  variant="danger"
                                  ml={2}
                                  py={2}
                                >
                                  <I18NRemove />
                                </LoadableButton>
                              )}
                            </MotionBox>
                          )}
                        </AnimatePresence>
                      </Flex>
                    </Box>
                  );
                };

                if (values.orderLines.length === 0) {
                  return (
                    <Form>
                      <FieldArray name="orderLines" render={renderOrderLines} />
                    </Form>
                  );
                }

                return (
                  <Form>
                    <FieldArray name="orderLines" render={renderOrderLines} />
                    <Flex mb="2">
                      <Box flex={1}>
                        <FormattedMessage id="total" defaultMessage="Total" />:
                      </Box>
                      <div data-testid="total-sum">
                        {loading ? (
                          <Spinner />
                        ) : (
                          <FormattedCurrency
                            value={
                              (discountApplied && !discountError
                                ? discountedOrder?.discount
                                    ?.totalAmountDiscounted
                                : sum) || 0
                            }
                          />
                        )}
                      </div>
                    </Flex>

                    <Flex column alignItems="center">
                      <Box style={{ maxWidth: '360px' }}>
                        {currentUser && (
                          <Box>
                            {giftCardsSelected && (
                              <Alert variant="info">
                                <FormattedMessage
                                  {...messages.applyDiscountCodeRestriction}
                                />
                              </Alert>
                            )}
                            <Field
                              name="code"
                              render={renderDiscountCodeField}
                            />
                          </Box>
                        )}

                        <FormGroup mb="sm">
                          <Label htmlFor="giftcard-email">
                            <FormattedMessage
                              id="modules.BuyGiftCards.emailForGiftCardLabel"
                              defaultMessage="Email address for the gift card"
                            />
                          </Label>
                          <Input
                            mb={3}
                            id="giftcard-email"
                            data-testid="giftcard-email"
                            name="customerEmail"
                            type="email"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.customerEmail}
                          />
                          <Text small mt={3} mb={3}>
                            <CheckboxStateless
                              checked={hidden}
                              onChange={handleToggle}
                              label={
                                <FormattedMessage
                                  {...messages.useTheSameEmail}
                                />
                              }
                            />
                          </Text>
                          {!hidden && (
                            <>
                              <Label htmlFor="giftcard-email">
                                <FormattedMessage
                                  id="modules.BuyGiftCards.emailForReceiptLabel"
                                  defaultMessage="Email address for the receipt"
                                />
                              </Label>
                              <Input
                                id="receipt-email"
                                data-testid="receipt-email"
                                name="receiptEmail"
                                type="email"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                value={values.receiptEmail}
                              />
                            </>
                          )}
                        </FormGroup>

                        <TermsOfSale
                          checked={values.tosAccepted}
                          onChange={checked => {
                            setFieldValue('tosAccepted', checked);
                          }}
                        />

                        {hasMultiplePaymentMethodTypes && (
                          <Box mb="xs">
                            <PaymentMethodTypeList
                              selected={values.paymentMethodType}
                              availablePaymentMethods={availablePaymentMethods}
                              onSelect={type =>
                                setFieldValue('paymentMethodType', type)
                              }
                            />
                          </Box>
                        )}

                        <Flex
                          justifyContent="center"
                          alignItems="center"
                          flexDirection="column"
                        >
                          <LoadableButton
                            type="submit"
                            name="checkout-btn"
                            variant="primary"
                            data-testid="regular-submit-button"
                            mb="xs"
                            shape="cool"
                            loading={
                              (placeValueCodeOrderResult.called ||
                                placeAnonymousValueCodeOrderResult.called ||
                                applyDiscountCodeResult.called ||
                                revokeDiscountCodeResult.called) &&
                              loading
                            }
                            responseState={
                              error || valueCodeOrderErrorCode || paymentError
                                ? 'error'
                                : 'success'
                            }
                            disabled={
                              !isValid ||
                              placeValueCodeOrderCheckoutResult.loading
                            }
                          >
                            <FormattedMessage {...messages.buyButton} />
                            <FormattedCurrency
                              value={
                                (discountApplied && !discountError
                                  ? discountedOrder?.discount
                                      ?.totalAmountDiscounted
                                  : sum) || 0
                              }
                            />
                          </LoadableButton>

                          {values.paymentMethodType ===
                            PaymentMethodType.card &&
                            currentUser &&
                            currentUser.paymentMethod &&
                            currentUser.paymentMethod.__typename === 'Card' && (
                              <Box mb="xs">
                                <ChargePaymentCardMessage
                                  last4={currentUser.paymentMethod.last4}
                                  displayBrand={
                                    currentUser.paymentMethod.displayBrand
                                  }
                                />{' '}
                                <AnchorButton
                                  data-testid="StripeCheckout__ChangeButton"
                                  onClick={handleChangeClick}
                                  disabled={
                                    !isValid ||
                                    placeValueCodeOrderCheckoutResult.loading
                                  }
                                  mute={!isValid}
                                  style={{
                                    pointerEvents: !isValid ? 'none' : undefined
                                  }}
                                >
                                  <FormattedMessage
                                    id="modules.BuyProduct.SummaryStep.changeButton"
                                    defaultMessage="Change"
                                  />
                                </AnchorButton>
                              </Box>
                            )}
                          {system && system.paymentProvider && (
                            <div>
                              <PaymentProviderInfo
                                paymentProvider={system.paymentProvider}
                              />
                            </div>
                          )}
                        </Flex>
                      </Box>
                    </Flex>
                  </Form>
                );
              }}
            </Formik>

            {privacyPageSlug && (
              <Box my={6} textAlign="center">
                <Anchor href={`/${intl.locale}/${privacyPageSlug}`}>
                  <FormattedMessage
                    id="modules.BuyGiftcard.Checkout.PersonalInformation"
                    defaultMessage="How we treat personal information"
                  />
                </Anchor>
              </Box>
            )}
          </>
        )}
      </Container>
    </Box>
  );
};

const MotionBox = motion(Box);

const FormError: FC = ({ children }) => {
  return (
    <Flex
      justifyContent="flex-start"
      mb="xxs"
      color="state.error"
      data-testid="Discount__FormError"
    >
      <Warning mr={2} />
      <Text as="span" typoStyle="xs">
        {children}
      </Text>
    </Flex>
  );
};

function getPaths({
  asPath,
  token,
  type
}: {
  asPath: string;
  token: string;
  type: PaymentDocumentType;
}) {
  return {
    successPath: `${asPath}?state=success&token=${token}&type=${type}`,
    cancelPath: `${asPath}?state=cancelled`
  };
}
